import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useAppDispatch } from "src/store/store";
import {
  setCryptoWithdrawalStage,
  resetWithdrawalThunk,
  setError,
  useWithdrawalSlice,
} from "src/store/features/withdrawal.slice";
import { useUpdateAddressTrFieldsMutation } from "src/store/features/api/notabene";
import AddressTrOwnership, {
  AddressTrOwnershipValue,
} from "src/components/wallets/addresses/AddressTrOwnership";
import AddressTrKind, {
  AddressTrKindValue,
} from "src/components/wallets/addresses/AddressTrKind";
import TrThirdPartyBlockModal from "src/components/transfers/TrThirdPartyBlockModal";
import { trackEvent } from "src/Amplitude";

/**
 * D4 — Travel-Rule backfill stage of the withdrawal modal.
 *
 * Reuses the D3 sub-step components inline so customers can fill the
 * missing TR data on an existing `requires_backfill` address before the
 * Notabene SafeConnect flow runs.
 *
 * Flow:
 *   - sub-step 1: AddressTrOwnership (with inline block modal)
 *   - sub-step 2: AddressTrKind (deferProof — see below)
 *   - submit: PATCH /withdrawal/address/:address/tr → advance stage to "Notabene"
 *   - cancel: setError + resetWithdrawalThunk
 *
 * Ownership proof is intentionally NOT collected here (`deferProof`). The
 * withdrawal-assist widget in the "Notabene" stage collects it itself —
 * wallet signature or self-declaration, per our `agentSections` config —
 * and `handleHostedComplete` forwards it upstream via the backend's
 * unhosted-proof relationship PATCH. Mounting connect-wallet here instead
 * (the pre-2026-07 behaviour) hit Notabene's hosted `/connect` page, which
 * ignores `agentSections` and offers signature-only, stranding customers
 * without live wallet access mid-withdrawal.
 */
const CryptoWithdrawalTrBackfill: React.FC = () => {
  const dispatch = useAppDispatch();
  const { walletAddress, selectedToken } = useWithdrawalSlice();
  const [updateTr, { isLoading }] = useUpdateAddressTrFieldsMutation();

  const [subStep, setSubStep] = useState<1 | 2>(1);
  const [ownership, setOwnership] = useState<AddressTrOwnershipValue>({
    isFirstParty: null,
  });
  const [kind, setKind] = useState<AddressTrKindValue>({
    destinationType: null,
  });
  const [blocked, setBlocked] = useState(false);

  const handleCancel = () => {
    trackEvent("TR Backfill Cancelled", { address: walletAddress });
    dispatch(setError("Withdrawal cancelled — recipient information required."));
    dispatch(resetWithdrawalThunk());
  };

  const ownershipReady =
    ownership.isFirstParty !== null &&
    !blocked &&
    !(ownership.isFirstParty === false && !(ownership.thirdPartyRecipientName || "").trim());

  // No walletVerificationReference gate for the self-hosted branch — proof
  // is deferred to the withdrawal-assist widget (see docblock).
  const kindReady =
    kind.destinationType !== null &&
    !(kind.destinationType === "Exchange" && !kind.destinationVasp);

  const handleSubmit = async () => {
    if (!walletAddress) {
      dispatch(setError("Cannot backfill — no address selected."));
      return;
    }
    try {
      await updateTr({
        address: walletAddress,
        body: {
          isFirstParty: ownership.isFirstParty ?? undefined,
          thirdPartyRecipientName: ownership.thirdPartyRecipientName,
          destinationType: kind.destinationType ?? undefined,
          destinationVasp: kind.destinationVasp,
          destinationVaspName: kind.destinationVaspName,
          walletVerificationReference: kind.walletVerificationReference,
        },
      }).unwrap();
      trackEvent("TR Backfill Submitted", { address: walletAddress });
      dispatch(setCryptoWithdrawalStage("Notabene"));
    } catch (err: any) {
      dispatch(setError(err?.message || "Failed to update Travel Rule fields."));
    }
  };

  return (
    <Box data-testid="crypto-withdrawal-tr-backfill" sx={{ width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Recipient information
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
        We need a few extra details about this address before we can process
        your withdrawal. This is a one-time step.
      </Typography>

      {subStep === 1 && (
        <AddressTrOwnership
          value={ownership}
          onChange={setOwnership}
          onBlocked={setBlocked}
          renderBlockModal={false}
        />
      )}
      {subStep === 2 && (
        <AddressTrKind
          value={kind}
          onChange={setKind}
          asset={selectedToken?.code}
          deferProof
        />
      )}

      {/* D5 — block modal renders inside the backfill flow too. */}
      <TrThirdPartyBlockModal
        open={blocked}
        scope="thirdPartyBlockBackfill"
        onCancel={handleCancel}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button onClick={handleCancel} data-testid="tr-backfill-cancel">
          Cancel
        </Button>
        {subStep === 1 ? (
          <Button
            variant="contained"
            disabled={!ownershipReady}
            onClick={() => setSubStep(2)}
            data-testid="tr-backfill-next">
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!kindReady || isLoading}
            onClick={handleSubmit}
            data-testid="tr-backfill-submit">
            {isLoading ? <CircularProgress size={18} /> : "Continue"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CryptoWithdrawalTrBackfill;
